<script setup lang="ts">
  import type {TransitionProps} from "vue";

  const route = useRoute();
  const currentTime = useCurrentTime();
  const eventsStore = useEventsStore();
  const {basicSetToken, optOutFromAll} = useFirebaseNotification();
  const {getSession} = useAuth();
  const {userLoggedIn} = useWadAuth();
  const notificationsStore = useNotificationsStore();
  const {notifyOtherDevices} = storeToRefs(notificationsStore);
  const {eventData, communityAvailable} = storeToRefs(eventsStore);
  const router = useRouter();
  const eventPartnersStore = useEventPartnersStore();
  const {needsOnboarding, requireEmailVerification} = useWadAuth();
  const {$bugsnag} = useNuxtApp();

  // `css: false`: the transition has no CSS and only orders the swap (old page out, then new page in, which the pages'
  // own scroll restore relies on). With CSS on, Vue waits for animation frames between the two, and iOS runs none for
  // about 470 ms after a swipe back, so the first frame it paints is still the page that was swiped away (TAL-11057).
  const pageTransition: TransitionProps = {name: 'fade', mode: 'out-in', css: false};
  // The agenda stays mounted while a session (or anything else) is open on top of it: rebuilding its ~500 rows
  // on every visit, back from a session included, was ~1 s of blocked main thread on a phone (TAL-11063).
  // Set here, not with definePageMeta: NuxtPage drops the KeepAlive wrapper on pages without the flag, which
  // would throw the cached agenda away on the first navigation. Only the agenda is cached, one event at a time.
  const pageKeepalive = {include: ['EventAgendaPage'], max: 1};

  // How long the 'reload-events' refresh is spread over. One purge tells every connected
  // attendee to refetch, so at 5,000 phones the old 3s window was a ~1,700 req/s spike onto
  // a just-purged edge. A minute costs nothing in freshness: the event payload is served
  // with max-age=60, so a client that ignored the signal entirely would still pick the
  // change up inside the same minute - and the first arrival re-warms the edge for the rest.
  const RELOAD_EVENTS_JITTER_MS = 60_000;

  // A move between two pages of the event this recently loaded does not fetch it again: the payload is served with
  // max-age=60 (so the answer would be the same bytes), and assigning it anew re-renders the page that just appeared,
  // which is what an iOS swipe back shows as a flicker. Changes still arrive through the SSE signals below.
  const EVENT_REFRESH_MIN_AGE_MS = 60_000;

  // Connectivity state drives the global offline banner.
  const isOnline = useOnline();
  // Hide the global banner on the scan page — the scanner has its own inline offline indicator.
  const showOfflineBanner = computed(() => !isOnline.value && route.name !== 'events-eventId-leads-scan');

  const eventId = ref<Number|null>(null);
  const showOnboardingModal = ref<boolean>(false);
  // Logout overlay flag, set by the user menu. Rendered here because the menu unmounts the instant
  // auth clears — keeping it at the app root lets the overlay survive until the redirect completes.
  const signingOut = useSignOutState();

  // Keyed on the path, not the whole route object: watching `route` fires on query-string changes
  // too, and the agenda mirrors its active filters into the URL (`navigateTo({query}, {replace})`)
  // — so every filter chip tap re-ran loadEvent, re-fetching the full /v2/events/{id} payload plus
  // /bookmarks, and reassigning eventData re-rendered the open filter dropdown mid-selection.
  // Every route change that does need the refresh (moving between an event's pages, switching
  // events) changes the path.
  watch([() => route.path, userLoggedIn], async () => {
    eventId.value = route.params.eventId ? Number(route.params.eventId) : null;
    if(!userLoggedIn.value) {
      return;
    }

    // Don't gate data loading on navigator.onLine (unreliable). Let the fetch run: online it's
    // fresh, offline the SW serves last-known from event-api-cache (NetworkFirst), and any failure
    // is swallowed by the catch below — so offline pages show cached data instead of nothing.
    try {
      // Logic for loading event data. Partners are no longer loaded here — only the pages that
      // actually render them call eventPartnersStore.ensureLoaded(), which fetches once per event.
      if(eventId.value) {
        if(eventData?.value?.id !== eventId.value) {
          await eventsStore.loadEvent(eventId.value);
        } else if(!eventsStore.isEventFresh(eventId.value as number, EVENT_REFRESH_MIN_AGE_MS)) {
          // (not fresh for another user either: the bookmarks in the store belong to whoever loaded them)
          eventsStore.loadEvent(eventId.value);
        }
      }

      // Logic for the onboarding modal
      if(eventId.value && needsOnboarding(eventId.value)) {
        await eventsStore.loadEventContactDetails(eventId.value);
        showOnboardingModal.value = needsOnboarding(eventId.value);
      } else {
        showOnboardingModal.value = false;
      }
    } catch(e) {}
  }, {immediate: true});

  // Logic for email verification. Keyed on the route NAME rather than the route object for the
  // same reason as the watcher above — the body only reads `route.name`, so waking it on every
  // query-string edit (the agenda's filter sync) was pure overhead. A name-only key is enough:
  // arriving at a gated route already redirects, so a later params-only change can't be reached
  // while verification is pending, and the flag itself is the other leg of the watch.
  // Attendees, partners, goodies and booth activities open with the agenda (TAL-11054). The navigation guard in
  // clientNavigation.global.ts handles links; this covers the event arriving after the page (a cold load) and the
  // preview being switched on while one of those pages is open.
  watch([() => route.name, () => eventData.value?.id, communityAvailable], ([name, loadedId, available]) => {
    if(available || typeof name !== 'string' || !COMMUNITY_ROUTE_NAMES.includes(name) || loadedId !== Number(route.params.eventId)) {
      return;
    }
    router.replace({name: 'events-eventId-not-available', params: {eventId: route.params.eventId}});
  }, {immediate: true});

  watch([requireEmailVerification, () => route.name], async () => {
    if(requireEmailVerification.value) {
      if(route.name && !['user-login', 'user-login', 'user-link-ticket', 'user-verification', 'legal'].includes(route.name)) {
        await navigateTo({name: 'user-verification'});
      }
    }
  },{immediate: true});

  const handleTechnicalMessage = async (e: CustomEvent) => {
    try {
      await handleTechnicalMessageAction(e);
    } catch (err) {
      $bugsnag?.notify(err as Error);
    }
  }

  const handleTechnicalMessageAction = async (e: CustomEvent) => {
    if(e.detail?.action == 'reload-events') {
      // A purge of one event's cache names that event in `event_id`; the broadcast itself still
      // reaches every connected client, so a client holding a different event has nothing to
      // refetch and skips it. No `event_id` (a purge of all events, or an older API) means
      // "reload whatever you have", exactly as before.
      const targetEventId = e.detail.event_id;
      if(targetEventId !== undefined && targetEventId !== null && Number(targetEventId) !== Number(eventId.value)) {
        return;
      }
      // event-api-cache is NetworkFirst, so a re-fetch already gets fresh data - the only
      // question is when. This signal goes to every connected attendee at once, and the
      // purge that triggers it has just emptied the Cloudflare cache, so the arrivals land
      // on a cold edge in front of an origin that is rebuilding the payload.
      if(eventId.value) {
        setTimeout(() => {
          if(!eventId.value) return;
          // Payload only: bookmarks and workshop counts are authenticated, uncacheable, and
          // have their own signals ('reload-bookmarks', 'update-counts'). Refetching them
          // here tripled the request count of every broadcast for no fresher data.
          eventsStore.loadEventPayload(eventId.value as number).catch(() => {});
          // Partners are fetched once per event now, so this signal is their only mid-session
          // refresh. Only reload for users who actually loaded them.
          if(eventPartnersStore.loadedEventId === eventId.value) {
            eventPartnersStore.loadEventPartners(eventId.value as number).catch(() => {});
          }
        }, Math.random() * RELOAD_EVENTS_JITTER_MS);
      }
    }else if(e.detail?.action == 'update-counts' && eventId.value) {
      // Buffered, not applied straight away: during the registration burst these arrive as a
      // stream and each payload is a full snapshot, so only the newest per session matters.
      eventsStore.queueWorkshopCountUpdate(e.detail.session_id, e.detail.count, e.detail.waitlist_full);
    }else if(e.detail?.action == 'reload-bookmarks' && eventId.value) {
      await eventsStore.getBookmarks(eventId.value as number);
    }else if(e.detail?.action === 'enabled-push-notifications') {
      if(!notifyOtherDevices.value) {
        await basicSetToken();
      }
      notifyOtherDevices.value = false;
    }else if(e.detail?.action === 'disabled-push-notifications') {
      if(!notifyOtherDevices.value) {
        await optOutFromAll(false);
      }
      notifyOtherDevices.value = false;
    }else if(e.detail?.action === 'reload-permissions') {
      await getSession();
    }
  }

  // The SSE stream came back after being dead. Anything published while it was down was never
  // delivered — Mercure is not replayed here — so the local workshop counts and bookmarks are
  // stale by an unknown amount and only a re-read of the server can recover them.
  //
  // Jittered and deduped, for the same reason as 'reload-events' above. The SSE plugin's own
  // backoff is jittered, but the browser's native EventSource retry is NOT: if Mercure restarts
  // mid-registration, every connected client reopens on the browser's schedule and fires this
  // at the same instant — turning one hub blip into a synchronised burst against
  // /workshops/counts and /bookmarks, which is exactly the traffic this work exists to bound.
  // 30 s, not 3 s: spread over 3 s, 5,000 reconnecting phones are about 3,300 requests per second
  // on the two slowest authenticated endpoints. A reconnecting phone's counts stay stale for at most
  // half a minute, and live 'update-counts' messages keep arriving in the meantime.
  const RECONCILE_JITTER_MS = 30_000;
  let reconcileTimer: ReturnType<typeof setTimeout> | null = null;

  const handleSseReconnected = () => {
    if (!eventId.value) return;
    // A flapping connection fires onopen repeatedly; one pending reconcile is enough, and the
    // fetch reads current state whenever it eventually runs.
    if (reconcileTimer !== null) return;

    reconcileTimer = setTimeout(async () => {
      reconcileTimer = null;
      if (!eventId.value) return;
      try {
        await eventsStore.reconcileWorkshopsCounts(eventId.value as number);
        await eventsStore.getBookmarks(eventId.value as number);
      } catch (err) {
        $bugsnag?.notify(err as Error);
      }
    }, Math.random() * RECONCILE_JITTER_MS);
  }

  onMounted(() => {
    currentTime.initAutoUpdate();
    window.addEventListener('wad:sse-technical-message', handleTechnicalMessage);
    window.addEventListener('wad:sse-reconnected', handleSseReconnected);
  })

  onBeforeUnmount(() => {
    currentTime.stopAutoUpdate();
    window.removeEventListener('wad:sse-technical-message', handleTechnicalMessage);
    window.removeEventListener('wad:sse-reconnected', handleSseReconnected);
    if (reconcileTimer !== null) {
      clearTimeout(reconcileTimer);
      reconcileTimer = null;
    }
  })


  // Chat used to bootstrap here: a CometChat login ran on app boot for every ticketed
  // user, almost none of whom ever opened a thread. Native chat loads with the thread
  // itself, so there is nothing to start up.

</script>

<template>
  <div class="wad4-app">
    <div class="wad4-ios-status-bar" aria-hidden="true"/>
    <!-- Top progress bar shown automatically during page navigations, so a slow connection
         reads as "loading" instead of a frozen/blank screen. -->
    <NuxtLoadingIndicator color="var(--wad4-c-background-bg-brand-solid)"/>
    <VitePwaManifest/>
    <PageAppHeader/>
    <client-only>
      <div v-if="showOfflineBanner" class="wad4-offline-banner" role="status">
        You're offline. Data may be out of date, and changes won't sync until you reconnect.
      </div>
    </client-only>
    <NuxtPage :transition="pageTransition" :keepalive="pageKeepalive"/>
    <client-only>
      <PageMainMenu/>
      <PageAccountMenu/>
      <CommonFirebaseNotificationsModal/>
      <VideoWadYouTubePlayer :pipInstance="true"/>
      <CommonUserOnboardingModal v-if="showOnboardingModal" />
      <!-- One-time nudge to "Plan with AI", after onboarding (or on the next visit for users who were already onboarded). -->
      <EventAgendaAiPlanIntro :blocked="showOnboardingModal"/>
      <!-- One-time line after the first bookmark: the session will remind you. -->
      <CommonReminderHint/>
      <!-- Blocking logout overlay. Stays up through auth-clear + redirect so the logged-in page
           never flashes back; the user menu owns the flag, this just renders it. -->
      <div v-if="signingOut" class="wad4-signout-overlay" role="status" aria-live="polite">
        <CommonWadSpinner />
        <p class="wad4-signout-overlay__text">Signing out...</p>
      </div>
    </client-only>
  </div>
</template>

<style lang="scss">
  // iOS 27 draws a Liquid Glass blur over the top of installed web apps, reaching well below the status bar, unless an
  // opaque position: fixed element sits in the top few pixels: WebKit samples the first ~10px for the status bar colour
  // and skips the blur only when it finds a fixed element there (the app header is sticky, not fixed). This 12px strip
  // in the header's colour sits behind the status bar, so nothing visible changes. Installed app, portrait, iOS only: in
  // landscape there is no status bar inset and the strip would cover the header.
  .wad4-ios-status-bar {
    display: none;
  }
  @supports (-webkit-touch-callout: none) {
    @media (display-mode: standalone) and (orientation: portrait) {
      .wad4-ios-status-bar {
        display: block;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        height: 12px;
        background-color: $wad4-c-background-bg-primary;
        pointer-events: none;
        z-index: 3;
      }
    }
  }

  // NuxtLoadingIndicator hardcodes `top: 0` as an inline style, which would render it in the
  // same strip as the iOS status bar/notch on the installed PWA (viewport-fit=cover). Push it
  // below the safe area like every other pinned-to-top element (header, user-menu, modals).
  .nuxt-loading-indicator {
    top: env(safe-area-inset-top, 0px) !important;
  }

  // Global offline indicator: sits in flow directly under the sticky header (sticky itself so it
  // stays visible while scrolling), telling the user why data isn't updating. The scanner keeps
  // its own inline indicator; this covers every other page.
  .wad4-offline-banner {
    position: sticky;
    top: var(--wad4-header-height-total);
    // Above the agenda's sticky page header (z-index 20, same sticky `top`) so the banner stays
    // on top instead of being painted over by the title when scrolled.
    z-index: 21;
    padding: $wad4-spacing-xs $wad4-spacing-lg;
    text-align: center;
    background: $wad4-c-background-bg-warning-primary;
    color: $wad4-c-text-text-warning;
    @include text-size-mixin('sm');
  }

  // Logout overlay: a transparent scrim over the whole app while signing out. Sits above the
  // avatar sidebar (z-index 1100) so it covers everything until the redirect lands.
  .wad4-signout-overlay {
    position: fixed;
    inset: 0;
    z-index: 1200;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: $wad4-spacing-lg;
    background: rgba(0, 0, 0, 0.4);

    &__text {
      margin: 0;
      @include text-size-mixin('md');
      color: #fff;
    }
  }
</style>

